/**
 * i18n configuration.
 *
 * URL strategy: language prefix `/en/*`, `/it/*`, `/pt/*`.
 * Default (Spanish) has no prefix: `/`, `/precios`, ...
 *
 * How to add a new language:
 *   1. Add code to SUPPORTED_LANGUAGES below.
 *   2. Create `locales/<code>.json` (can be empty `{}` — the translation script will fill it).
 *   3. Register the import here.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import es from './locales/es.json';
import en from './locales/en.json';
import it from './locales/it.json';
import pt from './locales/pt.json';

export const SUPPORTED_LANGUAGES = [
  { code: 'es', label: 'ES', name: 'Español', flag: '🇪🇸' },
  { code: 'en', label: 'EN', name: 'English', flag: '🇬🇧' },
  { code: 'it', label: 'IT', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', label: 'PT', name: 'Português', flag: '🇵🇹' },
];

export const DEFAULT_LANGUAGE = 'es';
export const SUPPORTED_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);
export const NON_DEFAULT_CODES = SUPPORTED_CODES.filter((c) => c !== DEFAULT_LANGUAGE);

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      es: { translation: es },
      en: { translation: en },
      it: { translation: it },
      pt: { translation: pt },
    },
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_CODES,
    interpolation: { escapeValue: false },
    detection: {
      order: ['path', 'localStorage', 'navigator'],
      lookupFromPathIndex: 0,
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    react: { useSuspense: false },
  });

/**
 * Extract the language code from a pathname.
 * `/en/precios` -> 'en'
 * `/precios`   -> 'es' (default)
 */
export function getLangFromPath(pathname) {
  const seg = (pathname || '/').split('/').filter(Boolean)[0];
  return NON_DEFAULT_CODES.includes(seg) ? seg : DEFAULT_LANGUAGE;
}

/**
 * Strip the language prefix from a pathname.
 * `/en/precios` -> `/precios`
 * `/precios`   -> `/precios`
 */
export function stripLangFromPath(pathname) {
  const parts = (pathname || '/').split('/').filter(Boolean);
  if (parts.length && NON_DEFAULT_CODES.includes(parts[0])) parts.shift();
  return '/' + parts.join('/');
}

/**
 * Build a localized path.
 * ('/precios', 'en') -> '/en/precios'
 * ('/precios', 'es') -> '/precios'
 */
export function localizePath(path, lang) {
  const clean = stripLangFromPath(path);
  if (lang === DEFAULT_LANGUAGE) return clean === '' ? '/' : clean;
  return `/${lang}${clean === '/' ? '' : clean}`;
}

export default i18n;
